<template>
  <div>
    <div class="row input-row">
      <div class="col-12 col-lg-6">
        <ifac-valid-input
          v-model="form.title"
          name="title"
          title="Title (Optional)"
          rules="max:32|latinScript"
          placeholder="e.g. Dr."
          tooltip="Dr., Mr., Mrs., etc"
          :privacy="form.titleVisibility"
          @privacy="form.titleVisibility = $event"
        ></ifac-valid-input>
      </div>
    </div>
    <div class="row justify-content-between input-row">
      <div class="col-12 col-lg-6">
        <ifac-valid-input
          v-model="form.name"
          name="name"
          title="First Name"
          rules="required|max:255|latinScript"
          required
          :privacy="form.fullNameVisibility"
          :privacy-allow-private="user.canSetNamePrivate"
          @privacy="form.fullNameVisibility = $event"
        ></ifac-valid-input>
      </div>
      <div class="col-12 col-lg-6">
        <ifac-valid-input
          v-model="form.surname"
          name="surname"
          title="Last Name"
          rules="required|max:255|latinScript"
          required
        ></ifac-valid-input>
      </div>
      <div class="col-12 mt-2" v-if="!user.canSetNamePrivate">
        <ifac-base-label
          :text-styles="['extra-small']"
          :class="['font-weight-light', 'font-italic']"
          text="
            As IFAC official in the current triennium,
            your name needs to be visible to all IFAC affiliates
          "
        />
      </div>
      <div class="col-12 mt-2" v-if="form.fullNameVisibility === 'private' && user.canSetNamePrivate">
        <ifac-base-label
          :text-styles="['extra-small']"
          :class="['font-weight-light', 'font-italic']"
          text="
            Your First Name and Last Name are set to private.
            Your profile is therefore not visible to anyone.
          "
        />
      </div>
    </div>
    <div class="row input-row">
      <div class="col-12 col-lg-6">
        <ifac-valid-input
          v-model="form.domesticName"
          name="domesticName"
          title="Name in any domestic characters (Optional)"
          rules="max:255"
          tooltip="Arabic, Chinese, Cyrillic, Hebrew, Japanese, Korean etc."
        ></ifac-valid-input>
      </div>
    </div>
    <div class="row input-row">
      <div class="col-12 col-lg-6">
        <ifac-dropdown
          title="Gender (Optional)"
          v-model="form.gender"
          :list="genders"
          :privacy="form.genderVisibility"
          @privacy="form.genderVisibility = $event"
        />
      </div>
    </div>
    <div class="row input-row">
      <div class="col-12 col-lg-6">
        <ifac-valid-input
          v-model="form.disability"
          name="disability"
          title="Disability (Optional)"
          rules="max:255"
          placeholder="e.g. Vision Impairment..."
          tooltip="Vision Impairment, Autism Spectrum Disorder, etc."
          :privacy="form.disabilityVisibility"
          @privacy="form.disabilityVisibility = $event"
        ></ifac-valid-input>
      </div>
    </div>
    <div class="row justify-content-between input-row">
      <div class="col-12 col-lg-6">
        <ifac-valid-input
          required
          v-model="form.affiliation"
          name="affiliation"
          title="Affiliation"
          rules="required|max:255"
          :privacy="form.affiliationVisibility"
          @privacy="form.affiliationVisibility = $event"
        />
      </div>
      <div class="col-12 col-lg-6">
        <ifac-dropdown
          title="Academia / Government / Industry (Optional)"
          v-model="form.industryAcademiaGovernment"
          :list="industryAcademiaGovernment"
          :privacy="form.industryAcademiaGovernmentVisibility"
          @privacy="form.industryAcademiaGovernmentVisibility = $event"
        ></ifac-dropdown>
      </div>
    </div>
    <div class="row justify-content-between input-row">
      <div class="col-12 col-lg-6">
        <ifac-valid-input
          v-model="form.jobPosition"
          name="jobPosition"
          title="Position (Optional)"
          rules="max:255"
          placeholder="e.g. Head of department..."
          tooltip="Head of department, Business analyst…"
          :privacy="form.jobPositionVisibility"
          @privacy="form.jobPositionVisibility = $event"
        ></ifac-valid-input>
      </div>
      <div class="col-12 col-lg-6">
        <ifac-dropdown
          title="Career Stage (Optional)"
          v-model="form.careerStage"
          :list="careerStages"
          :privacy="form.careerStageVisibility"
          @privacy="form.careerStageVisibility = $event"
        ></ifac-dropdown>
      </div>
    </div>
    <div class="row input-row">
      <div class="col-12 col-lg-6">
        <ifac-valid-input
          v-model="form.jobDescription"
          name="jobDescription"
          title="Job Description (Optional)"
          rules="max:255"
          placeholder="e.g. Full time researcher..."
          tooltip="Full time researcher, Engineer…"
          :privacy="form.jobDescriptionVisibility"
          @privacy="form.jobDescriptionVisibility = $event"
        ></ifac-valid-input>
      </div>
    </div>
    <div class="row input-row">
      <div class="col-12 col-lg-6">
        <ifac-search-dropdown
          title="Country / Region"
          :options="countries"
          placeholder="Select a country / region"
          :current="form.country"
          currentType="object"
          currentValue="name"
          optionValue="code"
          optionTitle="name"
          searchAgainst="name"
          emitType="string"
          emitValue="code"
          :errors="$v.country.$error ? ['This input is required'] : []"
          @selected="form.countryCode = $event"
          :privacy="form.countryVisibility"
          @privacy="form.countryVisibility = $event"
        />
      </div>
      <div
        v-if="countryNmo?.name && !memberOrgsLoading"
        class="col-12 mt-2"
      >
        <ifac-base-label
          :text-styles="['extra-small']"
          :class="['font-weight-light', 'font-italic']"
        >
          The IFAC National Member Organization in your Country/Region is
          <router-link :to="`/affiliates/officials/nmo/${form.countryCode}`">
            {{ countryNmo.name }}
          </router-link>
        </ifac-base-label>
      </div>
      <div
        v-if="countryAmo?.name && !memberOrgsLoading"
        class="col-12 mt-2"
      >
        <ifac-base-label
          :text-styles="['extra-small']"
          :class="['font-weight-light', 'font-italic']"
        >
          The IFAC Associate Member Organization in your Country/Region is
          <router-link :to="`/affiliates/officials/amo/${form.countryCode}`">
            {{ countryAmo.name }}
          </router-link>
        </ifac-base-label>
      </div>
    </div>
    <div class="row input-row">
      <div class="col-12 col-lg-6">
        <ifac-valid-input
          v-model="form.notesForSecretariat"
          name="notesForSecretariat"
          title="General information for the IFAC Secretariat (Optional)"
          rules="max:255"
          placeholder="Write here..."
          tooltip="Only visible to the IFAC Secretariat"
          privacy="private"
          :privacyReadOnly="true"
          :multiLine="true"
        ></ifac-valid-input>
      </div>
    </div>
  </div>
</template>

<script>
import {
  IfacValidInput,
  IfacBaseLabel,
  IfacSearchDropdown,
  IfacDropdown,
} from '@ifac/ui';
import { mapGetters, mapState } from 'vuex';
import { required } from 'vuelidate/lib/validators';

export default {
  components: {
    IfacValidInput,
    IfacSearchDropdown,
    IfacDropdown,
    IfacBaseLabel,
  },
  props: {
    value: {
      type: Object,
    },
  },
  created() {
    this.form.countryCode = this.value?.country?.code;
    this.emitValidation();
  },
  data() {
    return {
      form: this.value,
    };
  },
  computed: {
    ...mapGetters({
      user: 'auth/user',
      genders: 'genders/data',
      countries: 'countries/data',
      careerStages: 'careerStages/data',
      industryAcademiaGovernment: 'industryAcademiaGovernment/data',
    }),
    ...mapState({
      nmos: (state) => state.nationalMemberOrganizations.data,
      amos: (state) => state.associateMemberOrganizations.data,
      nmosLoading: (state) => state.nationalMemberOrganizations.loading,
      amosLoading: (state) => state.associateMemberOrganizations.loading,
    }),
    formIsValid() {
      return !this.$v.$invalid;
    },
    country() {
      return this.form?.countryCode;
    },
    memberOrgsLoading() {
      return this.nmosLoading || this.amosLoading;
    },
    countryNmo() {
      if (!this.form.countryCode) {
        return null;
      }

      return this.nmos.find((o) => o.country?.code === this.form.countryCode) ?? null;
    },
    countryAmo() {
      if (!this.form.countryCode) {
        return null;
      }

      return this.amos.find((o) => o.country?.code === this.form.countryCode) ?? null;
    },
  },
  validations: {
    country: { required },
  },
  watch: {
    form: {
      deep: true,
      handler(data) {
        this.$emit('form-changed', data);
      },
    },
    country() {
      this.$v.country.$touch();
    },
    formIsValid() {
      this.emitValidation();
    },
  },
  methods: {
    emitValidation() {
      this.$emit('valid', this.formIsValid);
    },
  },
};
</script>
